import { DEVELOPER_GROUP_LABELS, type DeveloperGroup } from '@company/shared'
import {
  X,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Code,
  TestTube,
  GitPullRequest,
} from 'lucide-react'
import { useState } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useActiveEmployees , useProjectMembers } from '@/hooks'
import type {
 Issue } from '@/hooks';
import {
  useBatchStageConfig,
  type BatchStageConfigInput,
  type BatchConfigureResult,
  type Stage          } from '@/hooks'
import { flattenMembers } from '@/lib/member-utils'


interface BatchStageConfigDialogProps {
  issues: Issue[]
  projectId: string
  onClose: () => void
  onSuccess?: () => void
}

const STAGE_CONFIG: Record<Stage, { label: string; icon: typeof Code; color: string }> = {
  development: { label: '开发', icon: Code, color: 'text-blue-600' },
  code_review: { label: '代码审查', icon: GitPullRequest, color: 'text-purple-600' },
  testing: { label: '测试', icon: TestTube, color: 'text-orange-600' },
}

/**
 * 批量配置阶段负责人对话框
 * Requirements: 6.1, 6.2, 6.3
 */
export function BatchStageConfigDialog({
  issues,
  projectId,
  onClose,
  onSuccess,
}: BatchStageConfigDialogProps) {
  const [configDevelopment, setConfigDevelopment] = useState(false)
  const [configCodeReview, setConfigCodeReview] = useState(false)
  const [configTesting, setConfigTesting] = useState(false)
  const [developmentAssigneeId, setDevelopmentAssigneeId] = useState<string>('')
  const [codeReviewAssigneeId, setCodeReviewAssigneeId] = useState<string>('')
  const [testingAssigneeId, setTestingAssigneeId] = useState<string>('')
  const [selectedGroup, setSelectedGroup] = useState<DeveloperGroup | ''>('')
  const [result, setResult] = useState<BatchConfigureResult | null>(null)

  const { data: groupedMembers } = useProjectMembers(projectId)
  const { data: activeEmployees } = useActiveEmployees()
  const batchStageConfig = useBatchStageConfig()

  const members = flattenMembers(groupedMembers)
  const filteredMembers = members.filter((m) => {
    if (selectedGroup && m.developerGroup !== selectedGroup) {
      return false
    }
    return true
  })

  // 创建员工ID到名称的映射
  const employeeNameMap = new Map<string, string>()
  activeEmployees?.forEach((emp) => {
    employeeNameMap.set(emp.id, emp.name)
  })

  const handleSubmit = () => {
    const input: BatchStageConfigInput = {
      issueIds: issues.map((i) => i.id),
    }

    if (configDevelopment) {
      input.developmentAssigneeId = developmentAssigneeId || null
    }
    if (configCodeReview) {
      input.codeReviewAssigneeId = codeReviewAssigneeId || null
    }
    if (configTesting) {
      input.testingAssigneeId = testingAssigneeId || null
    }

    batchStageConfig.mutate(input, {
      onSuccess: (data) => {
        setResult(data)
        if (data.success && data.failureCount === 0) {
          onSuccess?.()
        }
      },
    })
  }

  const hasAnyConfig = configDevelopment || configCodeReview || configTesting

  // 结果显示
  if (result) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-background w-full max-w-md rounded-lg p-6 shadow-lg">
          {result.failureCount === 0 ? (
            <>
              <div className="mb-4 flex items-center gap-2 text-green-600">
                <CheckCircle className="h-6 w-6" />
                <h3 className="text-lg font-medium">批量配置成功</h3>
              </div>
              <p className="text-muted-foreground mb-4 text-sm">
                已更新 {result.successCount} 个任务的阶段负责人配置
              </p>
            </>
          ) : (
            <>
              <div className="mb-4 flex items-center gap-2 text-red-600">
                <XCircle className="h-6 w-6" />
                <h3 className="text-lg font-medium">批量配置失败</h3>
              </div>
              <p className="text-muted-foreground mb-2 text-sm">以下任务配置失败:</p>
              <div className="mb-4 max-h-40 space-y-1 overflow-y-auto rounded-md border p-2">
                {result.results
                  .filter((r) => !r.success)
                  .map((r) => {
                    const issue = issues.find((i) => i.id === r.issueId)
                    return (
                      <div key={r.issueId} className="text-sm">
                        <span className="font-mono">{issue?.code || r.issueId.slice(0, 8)}</span>:{' '}
                        {r.error}
                      </div>
                    )
                  })}
              </div>
              <p className="text-muted-foreground text-sm">其他任务未做任何修改</p>
            </>
          )}
          <div className="mt-4 flex justify-end">
            <button
              onClick={onClose}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-4 py-2 text-sm font-medium"
            >
              确定
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background w-full max-w-lg rounded-lg shadow-lg">
        {/* 标题 */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-medium">批量配置阶段负责人</h3>
          <button onClick={onClose} className="hover:bg-muted rounded p-1">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-4">
          {/* 已选任务 */}
          <div className="mb-4">
            <div className="mb-2 text-sm font-medium">已选择 {issues.length} 个任务:</div>
            <div className="max-h-32 space-y-1 overflow-y-auto rounded-md border p-2">
              {issues.map((issue) => (
                <div key={issue.id} className="text-sm">
                  <span className="font-mono">{issue.code}</span> {issue.title}
                </div>
              ))}
            </div>
          </div>

          {/* 开发组筛选 */}
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium">开发组筛选</label>
            <Select
              value={selectedGroup || 'all'}
              onValueChange={(value) => setSelectedGroup(value === 'all' ? '' : value as DeveloperGroup)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="全部开发组" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部开发组</SelectItem>
                {(Object.keys(DEVELOPER_GROUP_LABELS) as DeveloperGroup[]).map((group) => (
                  <SelectItem key={group} value={group}>
                    {DEVELOPER_GROUP_LABELS[group]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 阶段配置 */}
          <div className="space-y-4">
            {/* 开发阶段 */}
            <StageConfigSection
              stage="development"
              config={STAGE_CONFIG.development}
              enabled={configDevelopment}
              onEnabledChange={setConfigDevelopment}
              selectedAssigneeId={developmentAssigneeId}
              onAssigneeChange={setDevelopmentAssigneeId}
              members={filteredMembers}
              employeeNameMap={employeeNameMap}
            />

            {/* 代码审查阶段 */}
            <StageConfigSection
              stage="code_review"
              config={STAGE_CONFIG.code_review}
              enabled={configCodeReview}
              onEnabledChange={setConfigCodeReview}
              selectedAssigneeId={codeReviewAssigneeId}
              onAssigneeChange={setCodeReviewAssigneeId}
              members={filteredMembers}
              employeeNameMap={employeeNameMap}
            />

            {/* 测试阶段 */}
            <StageConfigSection
              stage="testing"
              config={STAGE_CONFIG.testing}
              enabled={configTesting}
              onEnabledChange={setConfigTesting}
              selectedAssigneeId={testingAssigneeId}
              onAssigneeChange={setTestingAssigneeId}
              members={filteredMembers}
              employeeNameMap={employeeNameMap}
            />
          </div>

          {/* 警告 */}
          <div className="mt-4 flex items-start gap-2 rounded-md bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>批量配置将应用到所有选中的任务，任务状态变更时将自动切换到对应阶段的负责人</span>
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex justify-end gap-2 border-t px-4 py-3">
          <button
            onClick={onClose}
            className="hover:bg-muted rounded-md px-4 py-2 text-sm font-medium"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={batchStageConfig.isPending || !hasAnyConfig}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {batchStageConfig.isPending ? '处理中...' : '确认配置'}
          </button>
        </div>
      </div>
    </div>
  )
}

interface StageConfigSectionProps {
  stage: Stage
  config: { label: string; icon: typeof Code; color: string }
  enabled: boolean
  onEnabledChange: (enabled: boolean) => void
  selectedAssigneeId: string
  onAssigneeChange: (id: string) => void
  members: Array<{
    employeeId: string
    developerGroup: DeveloperGroup | null
    isLeader: boolean
  }>
  employeeNameMap: Map<string, string>
}

function StageConfigSection({
  config,
  enabled,
  onEnabledChange,
  selectedAssigneeId,
  onAssigneeChange,
  members,
  employeeNameMap,
}: StageConfigSectionProps) {
  const Icon = config.icon

  return (
    <div className="rounded-md border p-3">
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
          className="rounded"
        />
        <Icon className={`h-4 w-4 ${config.color}`} />
        <span className="text-sm font-medium">配置{config.label}阶段负责人</span>
      </label>
      {enabled && (
        <div className="mt-2">
          <Select
            value={selectedAssigneeId || 'clear'}
            onValueChange={(value) => onAssigneeChange(value === 'clear' ? '' : value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="清除负责人" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="clear">清除负责人</SelectItem>
              {members.map((m) => {
                const name = employeeNameMap.get(m.employeeId) || m.employeeId.slice(0, 8)
                return (
                  <SelectItem key={m.employeeId} value={m.employeeId}>
                    {name}
                    {m.developerGroup && ` (${DEVELOPER_GROUP_LABELS[m.developerGroup]})`}
                    {m.isLeader && ' ⭐'}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  )
}
